import React, { useState, useEffect } from "react";
import {
  X,
  Send,
  Bug,
  Lightbulb,
  TrendingUp,
  HelpCircle,
  CheckCircle,
  Shield,
  Camera,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthProvider";
import { feedbackService } from "../../services/feedbackService";
import { useToast } from "../../hooks/useToast";
import { motion, AnimatePresence } from "framer-motion";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { analytics } from "../../config/analytics";
import html2canvas from "html2canvas";

interface FeedbackFormData {
  category: "bug" | "feature" | "improvement" | "other";
  subject: string;
  message: string;
  priority: "low" | "medium" | "high" | "critical";
  metadata?: {
    url?: string;
    pathname?: string;
    referrer?: string;
    userAgent?: string;
    viewport?: string;
    timestamp?: string;
    screenshot?: string;
  };
}

const categoryConfig = {
  bug: {
    icon: Bug,
    color: "text-red-600",
    bgColor: "bg-red-100",
    label: "Bug Report",
  },
  feature: {
    icon: Lightbulb,
    color: "text-blue-600",
    bgColor: "bg-blue-100",
    label: "Feature Request",
  },
  improvement: {
    icon: TrendingUp,
    color: "text-green-600",
    bgColor: "bg-green-100",
    label: "Improvement",
  },
  other: {
    icon: HelpCircle,
    color: "text-gray-600",
    bgColor: "bg-gray-100",
    label: "Other",
  },
};

const priorityConfig = {
  low: { color: "text-gray-600", bgColor: "bg-gray-100", label: "Low" },
  medium: {
    color: "text-yellow-600",
    bgColor: "bg-yellow-100",
    label: "Medium",
  },
  high: { color: "text-orange-600", bgColor: "bg-orange-100", label: "High" },
  critical: { color: "text-red-600", bgColor: "bg-red-100", label: "Critical" },
};

interface FeedbackWidgetProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({
  isOpen,
  onClose,
}) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessState, setShowSuccessState] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    subject?: string;
    message?: string;
  }>({});
  const [formData, setFormData] = useState<FeedbackFormData>({
    category: "improvement",
    subject: "",
    message: "",
    priority: "medium",
  });
  const [recentFeedback, setRecentFeedback] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if viewport is mobile (screenshot only works on desktop)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen && user) {
      loadRecentFeedback();
    }
  }, [isOpen, user]);

  useEffect(() => {
    // Capture comprehensive metadata when widget opens
    // IMPORTANT: Preserve existing metadata (like screenshot) when updating
    if (isOpen) {
      setFormData((prev) => ({
        ...prev,
        metadata: {
          ...prev.metadata, // Preserve existing metadata including screenshot
          url: window.location.href,
          pathname: window.location.pathname,
          referrer: document.referrer || "direct",
          userAgent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          timestamp: new Date().toISOString(),
        },
      }));
    }
  }, [isOpen]);

  const loadRecentFeedback = async () => {
    try {
      const feedback = await feedbackService.getMyFeedback();
      // Ensure feedback is an array before calling slice
      if (Array.isArray(feedback)) {
        setRecentFeedback(feedback.slice(0, 3));
      } else {
        setRecentFeedback([]);
      }
    } catch (error) {
      console.error("Failed to load feedback history:", error);
      setRecentFeedback([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("FeedbackWidget: handleSubmit called", formData);

    const subject = formData.subject.trim();
    const message = formData.message.trim();
    const errors: { subject?: string; message?: string } = {};

    if (!subject) {
      errors.subject = "Subject is required";
    } else if (subject.length < 5) {
      errors.subject = "Subject must be at least 5 characters";
    } else if (subject.length > 200) {
      errors.subject = "Subject must be under 200 characters";
    }

    if (!message) {
      errors.message = "Details are required";
    } else if (message.length < 10) {
      errors.message = "Details must be at least 10 characters";
    } else if (message.length > 1500) {
      errors.message = "Details must be under 1500 characters";
    }

    setValidationErrors(errors);

    if (errors.subject || errors.message) {
      showError("Please fix the highlighted fields");
      return;
    }

    setIsSubmitting(true);

    try {
      // Execute reCAPTCHA if available
      let recaptchaToken: string | undefined;
      if (executeRecaptcha) {
        try {
          recaptchaToken = await executeRecaptcha("feedback_form");
          console.log("FeedbackWidget: reCAPTCHA token generated");
        } catch (error) {
          console.error("FeedbackWidget: reCAPTCHA execution failed:", error);
          // Continue without reCAPTCHA in case of error (backend will decide)
        }
      } else {
        console.log("FeedbackWidget: reCAPTCHA not available");
      }

      await feedbackService.submit(
        {
          ...formData,
          subject,
          message,
        },
        recaptchaToken,
      );
      console.log("FeedbackWidget: Feedback submitted successfully");

      // Track feedback submission analytics
      const sentiment =
        formData.category === "bug"
          ? "negative"
          : formData.category === "feature"
            ? "positive"
            : "neutral";
      analytics.trackFeedbackSubmitted(formData.category, sentiment);

      showSuccess("Thank you for your feedback! We'll review it soon.");
      setShowSuccessState(true);

      // Reset form
      setFormData({
        category: "improvement",
        subject: "",
        message: "",
        priority: "medium",
      });
      setValidationErrors({});

      // Close widget after delay
      setTimeout(() => {
        onClose();
        setShowSuccessState(false);
      }, 2000);

      // Reload feedback history
      loadRecentFeedback();
    } catch (error) {
      console.error("FeedbackWidget: Error submitting feedback:", error);
      const apiMessage =
        (error as any)?.response?.data?.error ||
        "Failed to submit feedback. Please try again.";
      showError(apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const captureScreenshot = async () => {
    try {
      setIsCapturing(true);

      // Find and temporarily hide the feedback modal and floating button
      // so we capture the actual page content underneath
      const feedbackModal = document.querySelector(
        '[data-feedback-modal="true"]',
      );
      const floatingButton = document.querySelector(
        '[data-feedback-button="true"]',
      );

      // Store original visibility
      const modalDisplay = feedbackModal
        ? (feedbackModal as HTMLElement).style.display
        : "";
      const buttonDisplay = floatingButton
        ? (floatingButton as HTMLElement).style.display
        : "";

      // Hide elements
      if (feedbackModal) (feedbackModal as HTMLElement).style.display = "none";
      if (floatingButton)
        (floatingButton as HTMLElement).style.display = "none";

      // Wait for DOM to update
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        scale: 0.7,
        logging: false,
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
      });

      // Restore visibility
      if (feedbackModal)
        (feedbackModal as HTMLElement).style.display = modalDisplay;
      if (floatingButton)
        (floatingButton as HTMLElement).style.display = buttonDisplay;

      const dataUrl = canvas.toDataURL("image/jpeg", 0.6);
      setFormData((prev) => ({
        ...prev,
        metadata: {
          ...(prev.metadata || {}),
          screenshot: dataUrl,
        },
      }));
      showSuccess("Screenshot attached to your feedback.");
    } catch (error) {
      console.error("Failed to capture screenshot:", error);
      showError("Could not capture screenshot. Please try again.");

      // Ensure elements are restored even on error
      const feedbackModal = document.querySelector(
        '[data-feedback-modal="true"]',
      );
      const floatingButton = document.querySelector(
        '[data-feedback-button="true"]',
      );
      if (feedbackModal) (feedbackModal as HTMLElement).style.display = "";
      if (floatingButton) (floatingButton as HTMLElement).style.display = "";
    } finally {
      setIsCapturing(false);
    }
  };

  if (!user) return null;

  // This component should only be used as a modal, not render its own button
  // The button is handled by FloatingFeedbackButton component
  if (!isOpen) return null;

  return (
    <>
      {/* Feedback Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            data-feedback-modal="true"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed z-40 bg-white dark:bg-gray-800 shadow-2xl overflow-hidden
              border-2 border-purple-200 dark:border-purple-700 flex flex-col
              /* Mobile: nearly full screen modal above bottom nav */
              inset-x-3 bottom-20 top-16 rounded-xl
              /* Desktop: positioned widget */
              md:inset-auto md:bottom-28 md:right-8 md:w-[420px] md:max-h-[calc(100vh-10rem)] md:rounded-2xl"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Send Feedback</h3>
                  <p className="text-sm opacity-90">Help us improve Vitly</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/30 rounded-lg transition-all
                    active:scale-95 touch-manipulation
                    bg-white/10 backdrop-blur-sm
                    hover:shadow-lg hover:bg-white/20"
                  aria-label="Close feedback form"
                  title="Close"
                >
                  <X className="w-7 h-7 stroke-[2.5]" />
                </button>
              </div>
            </div>

            {/* Success State */}
            {showSuccessState ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-8 text-center"
              >
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h4 className="text-lg font-semibold mb-2">Thank You!</h4>
                <p className="text-gray-600">
                  Your feedback has been submitted successfully.
                </p>
              </motion.div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="p-4 space-y-4 overflow-y-auto flex-1 md:max-h-[60vh]"
              >
                {/* Category Selection */}
                <div>
                  <span
                    id="category-label"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block"
                  >
                    Category
                  </span>
                  <div
                    className="grid grid-cols-2 gap-2"
                    role="group"
                    aria-labelledby="category-label"
                  >
                    {Object.entries(categoryConfig).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() =>
                            setFormData({ ...formData, category: key as any })
                          }
                          className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2
                            ${
                              formData.category === key
                                ? `border-purple-500 ${config.bgColor} dark:bg-opacity-20`
                                : "border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
                            }`}
                        >
                          <Icon className={`w-4 h-4 ${config.color}`} />
                          <span className="text-sm font-medium">
                            {config.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Priority Selection */}
                <div>
                  <span
                    id="priority-label"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block"
                  >
                    Priority
                  </span>
                  <div
                    className="flex gap-2"
                    role="group"
                    aria-labelledby="priority-label"
                  >
                    {Object.entries(priorityConfig).map(([key, config]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() =>
                          setFormData({ ...formData, priority: key as any })
                        }
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-all
                          ${
                            formData.priority === key
                              ? `${config.bgColor} ${config.color}`
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                      >
                        {config.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label
                      htmlFor="feedback-subject"
                      className="text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Subject *
                    </label>
                    <span
                      className={`text-xs ${formData.subject.trim().length >= 5 ? "text-green-600" : "text-gray-400"}`}
                    >
                      {formData.subject.trim().length}/5 min
                    </span>
                  </div>
                  <input
                    id="feedback-subject"
                    type="text"
                    value={formData.subject}
                    onChange={(e) =>
                      setFormData({ ...formData, subject: e.target.value })
                    }
                    placeholder="Brief description (min 5 characters)"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600
                      bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                      rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    maxLength={200}
                  />
                  {validationErrors.subject && (
                    <p className="text-xs text-red-600 mt-1">
                      {validationErrors.subject}
                    </p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label
                      htmlFor="feedback-details"
                      className="text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Details *
                    </label>
                    <span
                      className={`text-xs ${formData.message.trim().length >= 10 ? "text-green-600" : "text-gray-400"}`}
                    >
                      {formData.message.trim().length}/10 min
                    </span>
                  </div>
                  <textarea
                    id="feedback-details"
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    placeholder="Please provide more details (min 10 characters)..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600
                      bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                      rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    maxLength={1500}
                  />
                  {validationErrors.message && (
                    <p className="text-xs text-red-600 mt-1">
                      {validationErrors.message}
                    </p>
                  )}
                </div>

                {/* Tools */}
                <div className="flex flex-wrap gap-2 items-center">
                  {/* Screenshot button - desktop only */}
                  {!isMobile && (
                    <>
                      <button
                        type="button"
                        onClick={captureScreenshot}
                        disabled={isCapturing}
                        className={`flex items-center gap-1 px-3 py-1 text-sm rounded-lg transition-colors disabled:opacity-50
                          ${
                            formData.metadata?.screenshot
                              ? "text-green-600 bg-green-50 hover:bg-green-100 dark:text-green-400 dark:bg-green-900/30"
                              : "text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                          }`}
                      >
                        {formData.metadata?.screenshot ? (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            Screenshot attached
                          </>
                        ) : (
                          <>
                            <Camera className="w-4 h-4" />
                            {isCapturing ? "Capturing..." : "Attach screenshot"}
                          </>
                        )}
                      </button>
                      {formData.metadata?.screenshot && (
                        <button
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              metadata: {
                                ...prev.metadata,
                                screenshot: undefined,
                              },
                            }))
                          }
                          className="text-xs text-red-500 hover:text-red-700 dark:text-red-400"
                        >
                          Remove
                        </button>
                      )}
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowHistory(!showHistory)}
                    className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600
                      hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    History ({recentFeedback.length})
                  </button>
                </div>

                {/* Screenshot Preview - desktop only */}
                {!isMobile && formData.metadata?.screenshot && (
                  <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      Screenshot preview:
                    </p>
                    <img
                      src={formData.metadata.screenshot}
                      alt="Screenshot preview"
                      className="max-h-24 rounded border border-gray-200 dark:border-gray-600"
                    />
                  </div>
                )}

                {/* Recent Feedback */}
                {showHistory && recentFeedback.length > 0 && (
                  <div className="border-t pt-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Your Recent Feedback
                    </h4>
                    <div className="space-y-2">
                      {recentFeedback.map((item) => {
                        const category =
                          item.category as keyof typeof categoryConfig;
                        const config = categoryConfig[category];
                        return (
                          <div
                            key={item.id}
                            className="text-xs border rounded-lg p-2"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className={`font-medium ${config.color}`}>
                                {config.label}
                              </span>
                              <span
                                className={`px-2 py-0.5 rounded-full ${
                                  item.status === "resolved"
                                    ? "bg-green-100 text-green-600"
                                    : item.status === "in_progress"
                                      ? "bg-yellow-100 text-yellow-600"
                                      : "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {item.status}
                              </span>
                            </div>
                            <p className="text-gray-600 truncate">
                              {item.subject}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    formData.subject.trim().length < 5 ||
                    formData.message.trim().length < 10
                  }
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white 
                    py-2 px-4 rounded-lg font-medium hover:opacity-90 transition-opacity
                    disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Feedback
                    </>
                  )}
                </button>

                {/* Security indicator */}
                {executeRecaptcha && (
                  <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-500">
                    <Shield className="w-3 h-3" />
                    <span>Protected by reCAPTCHA</span>
                  </div>
                )}
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
